import type { ComponentType } from "react"
import type { SvgIconProps } from "@mui/material/SvgIcon"
import { SpaceDashboardOutlined } from "@mui/icons-material"

export interface PortfolioNavContext {
  account: string
  portfolioUrlSegment: string
  /** Resolved `portfolioId` query value: alias (`shortId`) when present, else portfolio UUID, else URL segment */
  portfolioIdParam: string
}

export interface PortfolioRootNavSubItem {
  label: string
  to: string
  controls?: { viewTypeSelector?: boolean }
  omit?: Record<string, unknown>
  hidden?: boolean
  disabled?: Record<string, unknown>
  newFeature?: { id: string }
}

export interface PortfolioRootNavItem {
  id: string
  label: string
  /** Segment under `/:account/p/:portfolio/`, or account-root path starting with `/`, or a builder for dynamic URLs */
  to: string | ((ctx: PortfolioNavContext) => string)
  icon?: string
  Icon?: ComponentType<SvgIconProps>
  subItems?: PortfolioRootNavSubItem[]
  hidden?: boolean
  omit?: Record<string, unknown>
  restrict?: string
  betaFeaturesEnabled?: boolean
  superuserOnly?: boolean
  featureGate?: "dashboard"
  /** If true, main nav uses target="_blank" (same-origin dashboard, etc.) */
  openInNewTab?: boolean
}

/** Sub-link after `computeSubItems` in selectors/links. */
export interface PortfolioNavExpandedSubLink extends PortfolioRootNavSubItem {
  id: string
}

/** Main nav link after `expandPortfolioNavLink` in selectors/links (resolved paths and ids). */
export interface PortfolioNavExpandedLink extends Omit<PortfolioRootNavItem, "to" | "subItems"> {
  id: string
  to: string
  subItems?: PortfolioNavExpandedSubLink[]
}

/**
 * Value for `portfolioId` in dashboard (and similar) query strings: human-readable alias when available,
 * otherwise the canonical portfolio id, otherwise whatever appears in the portfolio URL segment.
 */
export function resolvePortfolioIdQueryParam(
  portfolioData: { id?: string, shortId?: string } | null | undefined,
  portfolioUrlSegment: string,
): string {
  const alias = portfolioData?.shortId
  const id = portfolioData?.id
  if (alias) {
    return alias
  }
  if (id) {
    return id
  }
  return portfolioUrlSegment
}

export const PORTFOLIO_ROOT_NAVIGATION: PortfolioRootNavItem[] = [
  {
    id: "overview",
    label: "Overview",
    to: "overview",
    icon: "dashboard",
    subItems: [
      { label: "Performance", to: "performance", controls: { viewTypeSelector: true } },
      { label: "Risk", to: "risk", omit: { plan: "pro" }, controls: { viewTypeSelector: true } },
    ],
  },
  {
    id: "analyze",
    label: "Analyze",
    to: "analyze",
    icon: "bar-chart",
    subItems: [
      { label: "Intraday", to: "intraday", omit: { needIntraday: true, plan: "pro" }, newFeature: { id: "intraday" } },
      { label: "Assets", to: "assets", controls: { viewTypeSelector: true }, newFeature: { id: "asset-view" } },
      { label: "Factors", to: "factors", controls: { viewTypeSelector: true }, newFeature: { id: "factors-view" } },
      { label: "Trends", to: "trends", controls: { viewTypeSelector: true } },
      { label: "Compare", to: "compare", omit: { plan: "pro" }, controls: { viewTypeSelector: true } },
    ],
  },
  {
    id: "rebalance",
    label: "Rebalance",
    to: "rebalance",
    icon: "sliders",
    omit: { noExperiments: true },
    subItems: [
      { label: "Intraday", to: "intraday", omit: { needIntraday: true }, controls: { viewTypeSelector: false } },
      { label: "Optimizer", to: "optimizer", disabled: { plan: "pro" }, controls: { viewTypeSelector: true } },
      { label: "Simulator", to: "simulator", controls: { viewTypeSelector: true } },
      { label: "Experiments", to: "experiments", controls: { viewTypeSelector: true } },
    ],
  },
  {
    id: "dashboard",
    label: "Dashboard",
    to: ctx => `/dashboard?instrumentType=portfolio&instrumentId=${encodeURIComponent(ctx.portfolioIdParam)}`,
    Icon: SpaceDashboardOutlined,
    featureGate: "dashboard",
    openInNewTab: true,
  },
  {
    id: "experiments",
    label: "Experiments",
    to: "experiments",
    icon: "",
    hidden: true,
    subItems: [
      { label: "edit", to: "edit", hidden: true, controls: { viewTypeSelector: true } },
    ],
  },
]
